import './styles.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Row, Col, Container, Button } from 'reactstrap';
import InventoryProvider from '../InventoryProvider';
import MagBrandLink from '../MagBrandLink';
import ProductListViewItem from '../ProductListViewItem';
import { Product } from '~domain/product/Product';
import { accountSelector } from '~redux/account/selectors/account';
import { signup } from '~redux/auth/reducers/signup';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import {
  clearAllBrandProducts,
  clearBrandDetails,
  clearBrandProductsNextToken,
} from '~redux/brand/actions/details';
import {
  brandInfoSelector,
  brandProductsSelector,
} from '~redux/brand/selectors';
import { getBrandInfo, getProducts } from '~redux/brand/thunk/details';
import { Brand } from '~redux/brand/types';
import { isProtectedBrand } from '~utils/brand';
import { formatCurrency, formatDate } from '~utils/formatter';
import { getPublicImageUrl } from '~utils/image';
import { getFreeShippingPeriod, isFreeShipping } from '~utils/shipping';

type MagRelatedProductsProps = {
  product: Product;
};

const MagRelatedProducts: React.FC<MagRelatedProductsProps> = ({ product }) => {
  const dispatch = useDispatch();
  const [brandIconUrl, setBrandIconUrl] = useState('');
  const brand = useSelector(brandInfoSelector);
  const products = useSelector(brandProductsSelector);
  const user = useSelector(authUserSelector);
  const account = useSelector(accountSelector);

  const getBrandIconUrl = useCallback(async () => {
    if (!product?.brand?.brand_icon_imageKey) {
      return;
    }
    const url = getPublicImageUrl(product.brand.brand_icon_imageKey, {
      w: 100,
      h: 100,
    });
    setBrandIconUrl(url);
  }, [product?.brand?.brand_icon_imageKey]);

  useEffect(() => {
    getBrandIconUrl();
  }, [product?.brand?.brand_icon_imageKey]);

  useEffect(() => {
    dispatch(getBrandInfo(product.brand.id, !user));
    return () => {
      dispatch(clearBrandDetails());
    };
  }, [product.brand.id, user]);

  useEffect(() => {
    dispatch(
      getProducts(
        product.brand.id,
        '',
        {
          limit: 8,
          filter: {
            id: { ne: product.id },
          },
          sort: { field: 'product_display_order', direction: 'asc' },
        },
        false,
        !user
      )
    );
    return () => {
      dispatch(clearAllBrandProducts());
      dispatch(clearBrandProductsNextToken());
    };
  }, [product.brand.id, user]);

  const isProtected = useMemo(
    () => account && isProtectedBrand(product.brand as Brand, account),
    [product, account]
  );

  const handleBrandClick = useCallback(
    (event) => {
      if (!user) {
        event.preventDefault();
        dispatch(signup({ nextPath: window.location.pathname }));
        return;
      }
    },
    [user]
  );

  if (!brand) {
    return null;
  }

  return (
    <Row className="related-products p-4">
      <Col>
        <Container>
          <Row className="related-products-wrapper">
            <Col lg="3" md="12" className="my-4 mr-4">
              {brandIconUrl && (
                <div className="mb-4">
                  <MagBrandLink brandId={brand.id} className="brand-icon-link">
                    <img
                      src={brandIconUrl}
                      className="border"
                      alt="brand icon"
                    />
                  </MagBrandLink>
                </div>
              )}
              <div>
                <h5 translate="no">{brand.brand_name}</h5>
                <p>{brand.brand_address}</p>
                {!isProtected && user && (
                  <>
                    {brand.brand_minimum_buy != 0 ? (
                      <p>
                        最小発注金額：
                        {formatCurrency(brand.brand_minimum_buy)}
                      </p>
                    ) : (
                      <p>最小発注金額：設定なし</p>
                    )}
                    {!isFreeShipping() ||
                    user?.attributes?.profile !== 'buyer' ? (
                      <>
                        {brand.brand_shippinng_fee_criteria ? (
                          <p>
                            送料：{brand.brand_shippinng_fee_criteria / 10000}
                            万円以下の場合
                            {formatCurrency(brand.brand_shippinng_fee)}
                          </p>
                        ) : (
                          <p>送料：無料</p>
                        )}
                      </>
                    ) : (
                      <>
                        {brand.brand_shippinng_fee_criteria ? (
                          <>
                            <small className="free-shipping">
                              送料無料キャンペーン中（
                              {formatDate(getFreeShippingPeriod(), 'MM/dd')}
                              ご注文分まで）
                            </small>
                            <p>
                              送料：
                              <s className="mr-2">
                                {brand.brand_shippinng_fee_criteria / 10000}
                                万円以下の場合
                                {formatCurrency(brand.brand_shippinng_fee)}
                              </s>
                              <strong className="free-shipping">無料</strong>
                            </p>
                          </>
                        ) : (
                          <p>送料：無料</p>
                        )}
                      </>
                    )}
                  </>
                )}
                {!!brand.tags?.length && (
                  <Row xs="2">
                    {brand.tags.map((tag) => (
                      <Col className="brand-tag">
                        <p>{tag}</p>
                      </Col>
                    ))}
                  </Row>
                )}
                <Row className="mt-2">
                  <Col>
                    <Link
                      className="see-more-products bg-dark text-white"
                      to={`/brand/${product.product_brand_id}${location.search}`}
                      onClick={handleBrandClick}
                    >
                      <Button block>全ての商品を見る</Button>
                    </Link>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col>
              <Row xs="2" sm="2" md="4" lg="4">
                <InventoryProvider
                  productIds={products.map((product) => product.id)}
                >
                  {products.map((product, index) => (
                    <ProductListViewItem
                      product={product}
                      productTypes={product.producttype.items}
                      index={index}
                      unAuth
                      border={false}
                      showBrand={false}
                    />
                  ))}
                </InventoryProvider>
              </Row>
            </Col>
          </Row>
        </Container>
      </Col>
    </Row>
  );
};

export default MagRelatedProducts;
